import { create } from 'zustand';
import type {
  Alert,
  Asset,
  BaselineStatus,
  ComplianceFramework,
  DependencyEdge,
  IntelReport,
  OTDevice,
  PolicyRule,
  ScenarioInput,
  ThreatActor,
  ThreatCampaign,
  Vessel,
  Vulnerability,
} from '@/types';
import { loadAll, type FocusMode, type RegionManifest } from './manifest';

export interface SensorAppliance {
  sensor_id: string;
  asset_id: string;
  name: string;
  form_factor: string;
  deployment_mode: string;
  site: string;
  status: 'online' | 'offline' | 'degraded';
  throughput_mbps: number;
  uptime_pct: number;
  last_heartbeat: string;
}

/** All entity types acquire a `region_id` tag from the manifest loader. */
export type Tagged<T> = T & { region_id: string };

interface DataState {
  regions: RegionManifest[];
  focusModes: FocusMode[];
  assets: Tagged<Asset>[];
  edges: Tagged<DependencyEdge>[];
  alerts: Tagged<Alert>[];
  vessels: Tagged<Vessel>[];
  reports: Tagged<IntelReport>[];
  actors: Tagged<ThreatActor>[];
  campaigns: Tagged<ThreatCampaign>[];
  vulnerabilities: Tagged<Vulnerability>[];
  otDevices: Tagged<OTDevice>[];
  policies: Tagged<PolicyRule>[];
  baselines: Tagged<BaselineStatus>[];
  sensors: Tagged<SensorAppliance>[];
  scenarios: Tagged<ScenarioInput>[];
  compliance: Tagged<ComplianceFramework>[];
  loaded: boolean;
  loadError?: string;
  loadAll: () => Promise<void>;
  pushAlert: (a: Alert) => void;
  acknowledgeAlert: (id: string) => void;
  containAlert: (id: string) => void;
  setVessels: (v: Vessel[]) => void;
  addScenario: (s: ScenarioInput) => void;
  addAsset: (a: Asset, regionId?: string) => void;
  refreshBaselines: () => number;
  deploySensor: () => SensorAppliance | undefined;
}

export const useData = create<DataState>((set, get) => ({
  regions: [],
  focusModes: [],
  assets: [],
  edges: [],
  alerts: [],
  vessels: [],
  reports: [],
  actors: [],
  campaigns: [],
  vulnerabilities: [],
  otDevices: [],
  policies: [],
  baselines: [],
  sensors: [],
  scenarios: [],
  compliance: [],
  loaded: false,
  loadAll: async () => {
    try {
      const data = await loadAll();
      set({
        regions: data.manifest.regions,
        focusModes: data.focusModes,
        assets: data.assets as unknown as Tagged<Asset>[],
        edges: data.dependencies as unknown as Tagged<DependencyEdge>[],
        alerts: data.alerts as unknown as Tagged<Alert>[],
        reports: data.intelligence as unknown as Tagged<IntelReport>[],
        otDevices: data.otDevices as unknown as Tagged<OTDevice>[],
        policies: data.policies as unknown as Tagged<PolicyRule>[],
        baselines: data.baselines as unknown as Tagged<BaselineStatus>[],
        sensors: data.sensors as unknown as Tagged<SensorAppliance>[],
        scenarios: data.scenarios as unknown as Tagged<ScenarioInput>[],
        compliance: data.compliance as unknown as Tagged<ComplianceFramework>[],
        actors: data.actors as unknown as Tagged<ThreatActor>[],
        campaigns: data.campaigns as unknown as Tagged<ThreatCampaign>[],
        vulnerabilities: data.vulnerabilities as unknown as Tagged<Vulnerability>[],
        vessels: data.vessels as unknown as Tagged<Vessel>[],
        loaded: true,
      });
    } catch (err) {
      set({ loadError: err instanceof Error ? err.message : String(err) });
    }
  },
  pushAlert: (a) =>
    set((s) => ({
      alerts: [{ ...a, region_id: 'global' } as Tagged<Alert>, ...s.alerts].slice(0, 200),
    })),
  acknowledgeAlert: (id) =>
    set((s) => ({
      alerts: s.alerts.map((a) =>
        a.alert_id === id
          ? ({
              ...a,
              acknowledged_at: a.acknowledged_at ?? new Date().toISOString(),
              status:
                a.status === 'open' || a.status === 'investigating'
                  ? 'investigating'
                  : a.status,
            } as Tagged<Alert>)
          : a,
      ),
    })),
  containAlert: (id) =>
    set((s) => ({
      alerts: s.alerts.map((a) =>
        a.alert_id === id
          ? ({
              ...a,
              acknowledged_at: a.acknowledged_at ?? new Date().toISOString(),
              status: 'contained',
              enforcement_action: a.enforcement_action ?? 'quarantine',
            } as Tagged<Alert>)
          : a,
      ),
    })),
  setVessels: (v) =>
    set(() => ({
      vessels: v.map((vessel) => ({ ...vessel, region_id: 'global' }) as Tagged<Vessel>),
    })),
  addScenario: (s) =>
    set((st) => ({
      scenarios: [{ ...s, region_id: 'global' } as Tagged<ScenarioInput>, ...st.scenarios],
    })),
  addAsset: (a, regionId = 'global') =>
    set((st) => ({
      assets: [{ ...a, region_id: regionId } as Tagged<Asset>, ...st.assets],
    })),
  refreshBaselines: () => {
    let refreshed = 0;
    set((st) => ({
      baselines: st.baselines.map((b) => {
        refreshed += 1;
        return {
          ...b,
          observed_pairs: b.observed_pairs + (b.learning_complete ? 0 : 3),
          observed_protocols: b.observed_protocols,
          learning_complete: true,
          anomalies_24h: Math.max(0, b.anomalies_24h - 1),
        } as Tagged<BaselineStatus>;
      }),
    }));
    return refreshed;
  },
  deploySensor: () => {
    const state = get();
    const now = new Date().toISOString();
    const candidate = state.sensors.find((s) => s.status !== 'online');
    if (candidate) {
      const deployed: SensorAppliance = {
        ...candidate,
        status: 'online',
        uptime_pct: Math.max(candidate.uptime_pct, 99.9),
        last_heartbeat: now,
      };
      set((st) => ({
        sensors: st.sensors.map((s) =>
          s.sensor_id === candidate.sensor_id
            ? ({ ...deployed, region_id: s.region_id } as Tagged<SensorAppliance>)
            : s,
        ),
      }));
      return deployed;
    }

    const covered = new Set(state.sensors.map((s) => s.asset_id));
    const asset = state.assets.find((a) => !covered.has(a.asset_id)) ?? state.assets[0];
    if (!asset) return undefined;
    const deployed: SensorAppliance = {
      sensor_id: `SNS-DEMO-${Date.now()}`,
      asset_id: asset.asset_id,
      name: `Iris Sensor - ${asset.name.slice(0, 36)}`,
      form_factor: 'DIN-rail',
      deployment_mode: 'hybrid',
      site: `${asset.name} monitoring point`,
      status: 'online',
      throughput_mbps: 128,
      uptime_pct: 100,
      last_heartbeat: now,
    };
    set((st) => ({
      sensors: [{ ...deployed, region_id: asset.region_id } as Tagged<SensorAppliance>, ...st.sensors],
    }));
    return deployed;
  },
}));

// ───────────────────────────── UI store ─────────────────────────────

const FOCUS_STORAGE_KEY = 'iris.focus_mode';
const initialFocus = (() => {
  try {
    return localStorage.getItem(FOCUS_STORAGE_KEY) ?? 'global';
  } catch {
    return 'global';
  }
})();

interface UIState {
  selectedAssetId?: string;
  selectedAlertId?: string;
  contextOpen: boolean;
  layerVisibility: {
    assets: boolean;
    dependencies: boolean;
    vessels: boolean;
    threats: boolean;
    geofences: boolean;
    weather: boolean;
    infraNetwork: boolean;
  };
  scenarioOverlayActive: boolean;
  scenarioAffectedIds: string[];
  /** ID of the active focus mode (matches FocusMode.id). */
  focusModeId: string;
  tourOpen: boolean;
  tourStep: number;
  actionNotice?: {
    id: number;
    title: string;
    body?: string;
    kind?: 'info' | 'success' | 'warning';
  };
  setSelectedAsset: (id: string | undefined) => void;
  setSelectedAlert: (id: string | undefined) => void;
  setContextOpen: (open: boolean) => void;
  toggleLayer: (key: keyof UIState['layerVisibility']) => void;
  setScenarioOverlay: (ids: string[]) => void;
  clearScenarioOverlay: () => void;
  setFocusMode: (id: string) => void;
  startTour: () => void;
  closeTour: () => void;
  setTourStep: (step: number) => void;
  notifyAction: (notice: {
    title: string;
    body?: string;
    kind?: 'info' | 'success' | 'warning';
  }) => void;
  clearActionNotice: (id?: number) => void;
}

export const useUI = create<UIState>((set) => ({
  contextOpen: false,
  focusModeId: initialFocus,
  tourOpen: false,
  tourStep: 0,
  layerVisibility: {
    assets: true,
    dependencies: true,
    vessels: true,
    threats: true,
    geofences: false,
    weather: false,
    infraNetwork: true,
  },
  scenarioOverlayActive: false,
  scenarioAffectedIds: [],
  setSelectedAsset: (id) =>
    set({
      selectedAssetId: id,
      contextOpen: id ? true : false,
      selectedAlertId: undefined,
    }),
  setSelectedAlert: (id) =>
    set({
      selectedAlertId: id,
      contextOpen: id ? true : false,
      selectedAssetId: undefined,
    }),
  setContextOpen: (open) => set({ contextOpen: open }),
  toggleLayer: (key) =>
    set((s) => ({
      layerVisibility: { ...s.layerVisibility, [key]: !s.layerVisibility[key] },
    })),
  setScenarioOverlay: (ids) =>
    set({ scenarioOverlayActive: true, scenarioAffectedIds: ids }),
  clearScenarioOverlay: () =>
    set({ scenarioOverlayActive: false, scenarioAffectedIds: [] }),
  startTour: () => set({ tourOpen: true, tourStep: 0 }),
  closeTour: () => set({ tourOpen: false }),
  setTourStep: (step) => set({ tourStep: step }),
  notifyAction: (notice) =>
    set({
      actionNotice: {
        id: Date.now(),
        kind: 'info',
        ...notice,
      },
    }),
  clearActionNotice: (id) =>
    set((s) => {
      if (id && s.actionNotice?.id !== id) return s;
      return { actionNotice: undefined };
    }),
  setFocusMode: (id) => {
    try {
      localStorage.setItem(FOCUS_STORAGE_KEY, id);
    } catch {
      // ignore storage errors
    }
    set({
      focusModeId: id,
      selectedAssetId: undefined,
      selectedAlertId: undefined,
      contextOpen: false,
    });
  },
}));

// ──────────────────────── Focus-mode helpers ────────────────────────

/**
 * Resolve the active focus mode, returning a default global mode if not found.
 */
export function useActiveFocus(): FocusMode {
  const id = useUI((s) => s.focusModeId);
  const modes = useData((s) => s.focusModes);
  return (
    modes.find((m) => m.id === id) ??
    modes[0] ?? {
      id: 'global',
      label: 'Global',
      short_label: 'GLOBAL',
      description: '',
      regions: [],
      country_codes: null,
      bounds: [
        [-12, 35],
        [42, 71],
      ],
      center: [12, 53],
      zoom: 3.4,
      emphasis: { threat_actors: [], sectors: [], campaigns: [] },
      applicable_frameworks: [],
    }
  );
}

/** Return true if the entity passes the active focus's filter. */
export function inFocus(focus: FocusMode, entity: { region_id?: string; country?: string }): boolean {
  if (focus.country_codes && entity.country) {
    if (!focus.country_codes.includes(entity.country)) return false;
  }
  if (focus.regions.length > 0 && entity.region_id) {
    if (!focus.regions.includes(entity.region_id) && entity.region_id !== 'global') {
      return false;
    }
  }
  return true;
}

export function frameworkInFocus(
  focus: FocusMode,
  framework: Pick<ComplianceFramework, 'framework_id' | 'controls'>,
): boolean {
  if (focus.applicable_frameworks.length === 0) return true;
  const expanded = new Set(focus.applicable_frameworks);
  if (expanded.has('NIST_CSF_2')) expanded.add('NIST_CSF');
  if (expanded.has(framework.framework_id)) return true;
  for (const key of expanded) {
    if (framework.framework_id.startsWith(`${key}_`)) return true;
  }
  return framework.controls.some((control) => expanded.has(control.framework));
}
